import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { ChemicalRoute } from '../../types';

/** 电石制备 P&ID 详细图：生石灰+焦炭配料 → 矿热电炉熔炼 → 电石锅浇铸冷却 → 破碎筛分；炉气CO回收、炉渣外排 */
const media = {
  lime: { label: '生石灰(CaO)', light: '#9e9d24', dark: '#d4e157', width: 2.6 },
  coke: { label: '焦炭(C)', light: '#4e342e', dark: '#a1887f', width: 2.6 },
  melt: { label: '电石(熔融/成品)', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  co: { label: '炉气(CO,回收)', light: '#5c6bc0', dark: '#9fa8da', width: 2 },
  ash: { label: '炉渣/除尘灰', light: '#616161', dark: '#b0bec5', width: 2 },
};

const devices = [
  { id: 'j100', kind: 'mixer' as const, x: 242, y: 225, tag: '', name: '' },
  { id: 'f101', kind: 'furnace' as const, x: 320, y: 146, tag: 'F-101', name: '矿热电炉', note: '1800~2300℃' },
  { id: 'v104', kind: 'vDrum' as const, x: 430, y: -20, tag: 'V-104', name: '炉气除尘器' },
  { id: 'v103', kind: 'hDrum' as const, x: 470, y: 189, tag: 'V-103', name: '电石锅', note: '浇铸冷却' },
  { id: 'm101', kind: 'refiner' as const, x: 640, y: 186, tag: 'M-101', name: '破碎筛分机' },
];

const ports: PidPort[] = [
  { id: 'p-lime', label: '生石灰(CaO)', medium: 'lime', x: 0, y: 130, direction: 'in' as const },
  { id: 'p-coke', label: '焦炭(C)', medium: 'coke', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-co', label: 'CO炉气(回收)', medium: 'co', x: 540, y: 43, direction: 'out' as const },
  { id: 'p-product', label: '成品电石(防潮包装)', medium: 'melt', x: 790, y: 224, direction: 'out' as const },
  { id: 'p-ash', label: '炉渣/除尘灰', medium: 'ash', x: 430, y: 620, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-lime', fromSide: 'right', to: 'j100', toSide: 'top', medium: 'lime' },
  { id: 's02', from: 'p-coke', fromSide: 'right', to: 'j100', toSide: 'left', medium: 'coke' },
  { id: 's03', from: 'j100', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'coke', label: '混合料' },
  { id: 's04', from: 'f101', fromSide: 'top', to: 'v104', toSide: 'left', medium: 'co' },
  { id: 's05', from: 'v104', fromSide: 'right', to: 'p-co', toSide: 'left', medium: 'co' },
  { id: 's06', from: 'f101', fromSide: 'right', to: 'v103', toSide: 'left', medium: 'melt' },
  { id: 's07', from: 'v103', fromSide: 'right', to: 'm101', toSide: 'left', medium: 'melt' },
  { id: 's08', from: 'm101', fromSide: 'right', to: 'p-product', toSide: 'left', medium: 'melt' },
  { id: 's09', from: 'f101', fromSide: 'bottom', to: 'p-ash', toSide: 'left', medium: 'ash' },
];

const CalciumCarbideDiagram: React.FC<{ route: ChemicalRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="电石装置详细流程"
    subtitle="生石灰与焦炭按钙碳比配料 → 矿热电炉熔炼(1800~2300℃, CaO+3C→CaC₂+CO↑) → 电石锅浇铸冷却 → 破碎筛分 → 防潮包装；炉气CO除尘后回收作燃料/化工原料（电力接线按 P&ID 惯例不绘制）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default CalciumCarbideDiagram;
